import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Inbox, Loader2, ShieldAlert, CheckCircle2, XCircle,
  UserCheck, ExternalLink, Mail,
} from "lucide-react";
import { toast } from "sonner";
import CacheStatsWidget from "@/components/app/CacheStatsWidget";
import RemindersWidget from "@/components/app/RemindersWidget";
import OfstedUploadWidget from "@/components/app/OfstedUploadWidget";

const STATUS_TABS = [
  { key: "pending_pearsana_dispatch", label: "Pending" },
  { key: "contacted",                 label: "Contacted" },
  { key: "matched",                   label: "Matched" },
  { key: "dismissed",                 label: "Dismissed" },
];

const STATUS_STYLE = {
  pending_pearsana_dispatch: "border-amber-300 bg-amber-50 text-amber-800",
  contacted:                 "border-sky-300 bg-sky-50 text-sky-800",
  matched:                   "border-emerald-300 bg-emerald-50 text-emerald-800",
  dismissed:                 "border-stone-300 bg-stone-100 text-stone-600",
};

const STATUS_LABEL = {
  pending_pearsana_dispatch: "Pending",
  contacted: "Contacted",
  matched: "Matched",
  dismissed: "Dismissed",
};

function _leadKey(row) {
  if (row._source === "specialist_requests") {
    return `${row.user_id || ""}::${row.specialist_id || ""}`;
  }
  return row.email;
}

function _fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminLeads() {
  const { user, loading, login } = useAuth();
  const [state, setState] = useState({ checking: true, isAdmin: false });
  const [statusFilter, setStatusFilter] = useState("pending_pearsana_dispatch");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { setState({ checking: false, isAdmin: false }); return; }
    (async () => {
      try {
        await api.get("/admin/whoami");
        setState({ checking: false, isAdmin: true });
      } catch {
        setState({ checking: false, isAdmin: false });
      }
    })();
  }, [user, loading]);

  const fetchLeads = useCallback(async (status) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { status } });
      setRows(data.rows || []);
      setCounts(data.counts_by_status || {});
    } catch (err) {
      toast.error("Couldn't load leads. Try again?");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (state.isAdmin) fetchLeads(statusFilter);
  }, [state.isAdmin, statusFilter, fetchLeads]);

  const updateStatus = async (row, nextStatus) => {
    const key = _leadKey(row);
    if (!key) return;
    try {
      await api.patch(`/admin/leads/${encodeURIComponent(key)}/status`, {
        source: row._source,
        status: nextStatus,
      });
      toast.success(`Marked as ${STATUS_LABEL[nextStatus] || nextStatus}.`);
      fetchLeads(statusFilter);
    } catch {
      toast.error("Update failed — try again.");
    }
  };

  if (loading || state.checking) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 flex items-center gap-3 text-stone-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking your admin access…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-stone-400" />
        <h1 className="mt-4 font-editorial text-3xl">Admin only</h1>
        <p className="mt-2 text-stone-600">Sign in with an admin Google account to triage leads.</p>
        <Button data-testid="admin-leads-signin" onClick={login} className="mt-5">Sign in with Google</Button>
      </div>
    );
  }

  if (!state.isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-rose-500" />
        <h1 className="mt-4 font-editorial text-3xl">Access denied</h1>
        <p className="mt-2 text-stone-600">
          You're signed in as <span className="font-mono">{user.email}</span> — that email isn't on the admin allowlist.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="admin-leads-page">
      <Link to="/admin" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Admin home
      </Link>

      <div className="mt-4 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-[#4C1D95]" />
        <div className="label-uppercase text-[#4C1D95]">Leads triage</div>
      </div>
      <h1 data-testid="admin-leads-heading" className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight">
        Who's asked for help lately?
      </h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        Every WUA+ specialist request and Pearsana lead-capture click lands here.
        Real Pearsana routing is deferred — for now, triage each row by hand:
        email the family, mark them contacted, matched, or dismiss.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <CacheStatsWidget />
        <RemindersWidget />
        <OfstedUploadWidget />
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-leads-tabs">
        {STATUS_TABS.map((t) => {
          const isActive = t.key === statusFilter;
          const n = counts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatusFilter(t.key)}
              data-testid={`admin-leads-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-underground-sc tracking-wider border transition-colors ${
                isActive
                  ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                  : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95] hover:text-[#4C1D95]"
              }`}
            >
              {t.label}
              {typeof n === "number" && (
                <span className={`ml-2 text-[10px] ${isActive ? "text-white/80" : "text-stone-400"}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {busy && (
        <div className="mt-8 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading leads…
        </div>
      )}

      {!busy && rows.length === 0 && (
        <div
          data-testid="admin-leads-empty"
          className="mt-10 rounded-md border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-stone-600"
        >
          Nothing in this bucket. When someone taps "Request specialist match" it'll land here.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {rows.map((row) => {
          const key = _leadKey(row);
          const isPending = row.status === "pending_pearsana_dispatch";
          return (
            <div
              key={`${row._source}-${key}-${row.created_at}`}
              data-testid={`admin-lead-${row._source}-${key}`}
              className="rounded-lg border border-stone-200 bg-white p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center flex-wrap gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-underground-sc tracking-wider ${STATUS_STYLE[row.status] || STATUS_STYLE.pending_pearsana_dispatch}`}>
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                  <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                    {row._lead_type === "authed" ? "Signed-in" : "Anon"} · {_fmtDate(row.created_at)}
                  </span>
                </div>
                <div className="mt-2 font-editorial text-lg text-stone-900">
                  {row.specialist_title || "Grades / Predicted-grade uplift"}
                </div>
                <div className="mt-1 text-sm text-stone-700 flex flex-wrap gap-x-3 gap-y-1">
                  {row.user_name && <span>{row.user_name}</span>}
                  {row.user_email && (
                    <a
                      href={`mailto:${row.user_email}`}
                      className="text-[#4C1D95] hover:underline underline-offset-2 inline-flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" /> {row.user_email}
                    </a>
                  )}
                  {row.email && !row.user_email && (
                    <a
                      href={`mailto:${row.email}`}
                      className="text-[#4C1D95] hover:underline underline-offset-2 inline-flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" /> {row.email}
                    </a>
                  )}
                  {row.target_grade && <span className="text-stone-500">· target: {row.target_grade}</span>}
                  {row.intended_degree && <span className="text-stone-500">· degree: {row.intended_degree}</span>}
                </div>
                {row.gap_context && Object.keys(row.gap_context).length > 0 && (
                  <details className="mt-2 text-xs text-stone-500">
                    <summary className="cursor-pointer hover:text-stone-800">Show gap context</summary>
                    <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] bg-stone-50 p-2 rounded">
                      {JSON.stringify(row.gap_context, null, 2)}
                    </pre>
                  </details>
                )}
                {row.note && (
                  <div className="mt-2 text-xs text-stone-500 italic">"{row.note}"</div>
                )}
                {row.triaged_by && (
                  <div className="mt-2 text-[10px] font-underground-sc tracking-wider text-stone-400">
                    triaged by {row.triaged_by} · {_fmtDate(row.triaged_at)}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 shrink-0">
                {isPending && (
                  <>
                    <button
                      type="button"
                      onClick={() => updateStatus(row, "contacted")}
                      data-testid={`admin-lead-${key}-contacted`}
                      className="px-3 py-1.5 rounded-md text-xs font-underground-sc tracking-wider border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
                    >
                      <CheckCircle2 className="h-3 w-3 inline mr-1" /> Contacted
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStatus(row, "dismissed")}
                      data-testid={`admin-lead-${key}-dismissed`}
                      className="px-3 py-1.5 rounded-md text-xs font-underground-sc tracking-wider border border-stone-300 bg-white text-stone-600 hover:bg-stone-50"
                    >
                      <XCircle className="h-3 w-3 inline mr-1" /> Dismiss
                    </button>
                  </>
                )}
                {row.status === "contacted" && (
                  <button
                    type="button"
                    onClick={() => updateStatus(row, "matched")}
                    data-testid={`admin-lead-${key}-matched`}
                    className="px-3 py-1.5 rounded-md text-xs font-underground-sc tracking-wider border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  >
                    <UserCheck className="h-3 w-3 inline mr-1" /> Matched
                  </button>
                )}
                {row.status !== "pending_pearsana_dispatch" && row.status !== "dismissed" && (
                  <button
                    type="button"
                    onClick={() => updateStatus(row, "pending_pearsana_dispatch")}
                    className="px-3 py-1.5 rounded-md text-xs font-underground-sc tracking-wider border border-stone-300 bg-white text-stone-600 hover:bg-stone-50"
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500 leading-relaxed">
        Backend endpoints: <span className="font-mono">GET /api/admin/leads</span> ·{" "}
        <span className="font-mono">PATCH /api/admin/leads/&#123;key&#125;/status</span>.
        Rows come from two Mongo collections (<span className="font-mono">specialist_requests</span> + <span className="font-mono">pearsana_leads</span>);
        real Pearsana dispatch remains deferred until the partner API specs arrive.
      </div>
    </div>
  );
}
